<template>
  <div class="max-w-4xl mx-auto">
    <Head title="Crear Transacción de Caja" />

    <!-- Header -->
    <div class="flex items-center gap-4 mb-8">
      <button
        class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
        title="Volver atrás"
        @click="back()"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-3xl font-bold">
        <Link
          class="text-teal-600 hover:text-teal-700 transition-colors"
          href="/cash-transactions"
        >
          Transacciones de Caja
        </Link>
        <span class="text-gray-400 font-medium mx-2">/</span>
        <span>Crear</span>
      </h1>
    </div>

    <!-- Form -->
    <div class="max-w-4xl bg-white rounded-lg shadow-sm border border-gray-200">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="block text-gray-700 mb-2">Tipo de Transacción</label>
            <select
              v-model="form.type"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Seleccionar tipo</option>
              <option value="withdrawal">Retiro de efectivo</option>
              <option value="deposit">Depósito de efectivo</option>
              <option value="transfer_out">Transferencia entre cajas</option>
            </select>
            <div v-if="form.errors.type" class="text-red-500 text-sm mt-1">{{ form.errors.type }}</div>
          </div>

          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="block text-gray-700 mb-2">Caja</label>
            <select
              v-model="form.cash_register_id"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Seleccionar caja</option>
              <option
                v-for="register in openCashRegisters"
                :key="register.id"
                :value="register.id"
              >
                {{ register.name }} (Saldo: {{ formatCurrency(register.current_balance) }})
              </option>
            </select>
            <div v-if="form.errors.cash_register_id" class="text-red-500 text-sm mt-1">{{ form.errors.cash_register_id }}</div>
          </div>

          <text-input
            v-model="form.amount"
            :error="form.errors.amount"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Monto (USD)"
            type="number"
            step="0.01"
            min="0.01"
          />

          <!-- Currency Display -->
          <div class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="block text-gray-700 mb-2">Equivalentes</label>
            <div class="bg-gray-50 p-3 rounded-lg">
              <div class="text-sm space-y-1">
                <p><strong>BS:</strong> {{ formatCurrency(calculateBsAmount, 'VES') }}</p>
                <p><strong>COP:</strong> {{ formatCurrency(calculateCopAmount, 'COP') }}</p>
              </div>
            </div>
          </div>

          <div class="pb-8 pr-6 w-full">
            <label class="block text-gray-700 mb-2">Descripción</label>
            <textarea
              v-model="form.description"
              class="form-textarea w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              rows="3"
              placeholder="Describe el motivo de la transacción..."
            ></textarea>
            <div v-if="form.errors.description" class="text-red-500 text-sm mt-1">{{ form.errors.description }}</div>
          </div>

          <text-input
            v-if="['transferencia', 'transferencia-usd', 'zelle', 'pago-movil'].includes(form.type)"
            v-model="form.reference"
            :error="form.errors.reference"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Referencia"
          />

          <div v-if="form.type === 'transfer_out'" class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="block text-gray-700 mb-2">Caja Destino</label>
            <select
              v-model="form.transfer_to_register_id"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Seleccionar caja destino</option>
              <option
                v-for="register in availableDestinationRegisters"
                :key="register.id"
                :value="register.id"
              >
                {{ register.name }}
              </option>
            </select>
            <div v-if="form.errors.transfer_to_register_id" class="text-red-500 text-sm mt-1">{{ form.errors.transfer_to_register_id }}</div>
          </div>
        </div>

        <!-- Submit -->
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button
            :loading="form.processing"
            class="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors"
            type="submit"
          >
            Crear Transacción
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
  },

  layout: Layout,

  props: {
    openCashRegisters: {
      type: Array,
      default: () => []
    },
    exchangeRates: {
      type: Object,
      default: () => ({ usdToBs: 1, usdToCop: 1 })
    }
  },

  data() {
    return {
      form: this.$inertia.form({
        type: '',
        cash_register_id: '',
        amount: '',
        description: '',
        reference: '',
        transfer_to_register_id: '',
      }),
    }
  },

  computed: {
    calculateBsAmount() {
      return this.form.amount ? parseFloat(this.form.amount) * this.exchangeRates.usdToBs : 0
    },

    calculateCopAmount() {
      return this.form.amount ? parseFloat(this.form.amount) * this.exchangeRates.usdToCop : 0
    },

    availableDestinationRegisters() {
      if (!this.form.cash_register_id) return []

      return this.openCashRegisters.filter(register =>
        register.id !== parseInt(this.form.cash_register_id)
      )
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    formatCurrency(value, currency = 'USD') {
      const currencies = {
        'USD': 'en-US',
        'VES': 'es-VE',
        'COP': 'es-CO'
      }

      const symbols = {
        'USD': 'USD',
        'VES': 'VES',
        'COP': 'COP'
      }

      return new Intl.NumberFormat(currencies[currency] || 'en-US', {
        style: 'currency',
        currency: symbols[currency] || 'USD'
      }).format(value || 0)
    },

    store() {
      this.form.post('/cash-transactions')
    },
  }
}
</script>